import React, { useEffect } from 'react';

/**
 * Reusable confirmation modal.
 *
 * Props:
 *   show        - boolean
 *   title       - string (default "Are you sure?")
 *   message     - string
 *   confirmText - string (default "Delete")
 *   cancelText  - string (default "Cancel")
 *   danger      - boolean — red confirm button (default true)
 *   onConfirm   - () => void
 *   onCancel    - () => void
 */
export default function ConfirmModal({
    show,
    title = 'Are you sure?',
    message,
    confirmText = 'Delete',
    cancelText = 'Cancel',
    danger = true,
    onConfirm,
    onCancel,
}) {
    // Close on Escape key
    useEffect(() => {
        if (!show) return;
        const handler = (e) => { if (e.key === 'Escape') onCancel?.(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [show, onCancel]);

    if (!show) return null;

    return (
        <div
            onClick={onCancel}
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: 'rgba(0,0,0,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: '#fff',
                    borderRadius: 14,
                    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
                    padding: '32px 28px 24px',
                    width: '100%',
                    maxWidth: 420,
                    textAlign: 'center',
                }}
            >
                {/* Icon */}
                <div style={{
                    width: 52, height: 52, borderRadius: '50%',
                    background: danger ? '#fee2e2' : '#fef3c7',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 16px',
                }}>
                    {danger ? (
                        <svg viewBox="0 0 24 24" fill="none" width="26" height="26">
                            <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" fill="none" width="26" height="26">
                            <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                </div>

                {/* Title */}
                <h5 style={{ fontWeight: 700, color: '#1a3a5c', marginBottom: 8, fontSize: 17 }}>
                    {title}
                </h5>

                {/* Message */}
                {message && (
                    <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 24, lineHeight: 1.55, whiteSpace: 'pre-line', textAlign: 'left' }}>
                        {message}
                    </div>
                )}

                {/* Buttons */}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                    <button
                        onClick={onCancel}
                        style={{
                            flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid #d1d5db',
                            background: '#fff', color: '#374151', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                        }}
                    >
                        {cancelText}
                    </button>
                    <button
                        onClick={onConfirm}
                        style={{
                            flex: 1, padding: '10px 0', borderRadius: 8, border: 'none',
                            background: danger ? '#dc2626' : '#1B75F0',
                            color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                        }}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
}
